import { css } from '@emotion/react'
import { VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useErrorHandler } from 'components/error'
import { CheckboxField, FormRenderProps, RadioGroupField, TextAreaField } from 'components/form'
import FormModal from 'components/modals/FormModal'
import createDecorator from 'final-form-calculate'
import { useCancelarAgendamentoMutation } from 'graphql/hooks.generated'
import { useConfiguracoes } from 'hooks/configuracoes'
import { useMemo } from 'react'
import {
  JustificativaCancelamentoAgendaComboEnum as JustificativaEnum,
  JustificativaCancelamentoAgendaRecord,
} from 'types/enums'
import { metaPath } from 'util/metaPath'
import { CancelamentoAgendamentoModel, EventoAgendaLotacao, TipoAgendamento } from 'view/agenda/model-agenda'

import { calculatorJustificativaCancelamento } from './calculator'
import { validateJustificativaCancelamento } from './validator-cancelarAgendamento'

interface CancelarAgendamentoModalProps {
  event: Pick<
    EventoAgendaLotacao,
    'id' | 'tipo' | 'nomeCidadao' | 'motivoReserva' | 'enviouEmailCidadao' | 'emailCidadao'
  >
  onAction?(): void
  open: boolean
  setOpen(open: boolean): void
  justificativa?: JustificativaEnum
  justificativaOutra?: string
}

const path = metaPath<CancelamentoAgendamentoModel>()
const decorator = [createDecorator(calculatorJustificativaCancelamento(path))]

export default function CancelarAgendamentoModal(props: Readonly<CancelarAgendamentoModalProps>) {
  const { event, open, setOpen, onAction, justificativa, justificativaOutra } = props

  const alert = useAlert()
  const handleException = useErrorHandler()
  const [cancelarAgendamento] = useCancelarAgendamentoMutation()
  const {
    configuracoes: { smtpConfigurado },
  } = useConfiguracoes()

  const handleClose = () => setOpen(false)

  const solicitarJustificativa = !justificativa
  const showCheckboxEnviarEmail =
    event.tipo === TipoAgendamento.CONSULTA && !event.enviouEmailCidadao && event.emailCidadao && smtpConfigurado

  const handleSubmit = (values: CancelamentoAgendamentoModel) => {
    cancelarAgendamento({
      variables: {
        input: {
          id: event.id,
          justificativa:
            JustificativaCancelamentoAgendaRecord[values.justificativa].justificativaCancelarAgendamentoEnum,
          justificativaOutra: values.justificativaOutra,
          enviarEmailCidadao: values.enviarEmailCidadao,
        },
      },
    })
      .then(() => {
        alert(
          'success',
          event.tipo === TipoAgendamento.CONSULTA
            ? `Agendamento de ${event.nomeCidadao.titleCase()} foi cancelado.`
            : `Reserva de horário para ${event.motivoReserva.titleCase()} foi cancelada`
        )
        handleClose()
        onAction?.()
      })
      .catch(handleException)
  }

  const renderBody = (formProps: FormRenderProps<CancelamentoAgendamentoModel>) => {
    const { values } = formProps
    return (
      <VFlow>
        {event.tipo === TipoAgendamento.CONSULTA && solicitarJustificativa && (
          <RadioGroupField
            name={path.justificativa}
            required
            label='Justificativa de cancelamento'
            lines={4}
            options={[
              {
                value: JustificativaEnum.LICENCA_MEDICA,
                label: JustificativaCancelamentoAgendaRecord[JustificativaEnum.LICENCA_MEDICA].descricao,
              },
              {
                value: JustificativaEnum.CIDADAO_CANCELOU,
                label: JustificativaCancelamentoAgendaRecord[JustificativaEnum.CIDADAO_CANCELOU].descricao,
              },
              {
                value: JustificativaEnum.REUNIAO_NAO_PROGRAMADA,
                label: JustificativaCancelamentoAgendaRecord[JustificativaEnum.REUNIAO_NAO_PROGRAMADA].descricao,
              },
              {
                value: JustificativaEnum.OUTRA,
                label: JustificativaCancelamentoAgendaRecord[JustificativaEnum.OUTRA].descricao,
              },
            ]}
          />
        )}
        {values.justificativa === JustificativaEnum.OUTRA && solicitarJustificativa && (
          <TextAreaField name={path.justificativaOutra} required label='Especifique a justificativa' maxLength={500} />
        )}
        {showCheckboxEnviarEmail && (
          <CheckboxField
            style={css`
              margin-left: -0.2rem;
            `}
            name={path.enviarEmailCidadao}
            label='Enviar e-mail de cancelamento ao cidadão'
          />
        )}
      </VFlow>
    )
  }

  const initialValues: CancelamentoAgendamentoModel = useMemo(
    () => ({
      id: event.id,
      justificativa: justificativa ?? (event.tipo === TipoAgendamento.RESERVA ? JustificativaEnum.OUTRA : undefined),
      justificativaOutra,
      enviarEmailCidadao: event.tipo !== TipoAgendamento.RESERVA && event.enviouEmailCidadao,
    }),
    [event.enviouEmailCidadao, event.id, event.tipo, justificativa, justificativaOutra]
  )

  return (
    <FormModal<CancelamentoAgendamentoModel>
      icon={{
        name: 'exclamationTriangleOutline',
        fill: 'danger',
      }}
      title={
        solicitarJustificativa
          ? `Justificativa para cancelamento agendamento de ${event.tipo}`
          : 'Deseja excluir o agendamento?'
      }
      buttons={
        solicitarJustificativa
          ? { confirm: { label: 'Salvar e cancelar agendamento', kind: 'danger' }, cancel: 'Cancelar justificativa' }
          : { confirm: { label: 'Excluir', kind: 'danger' }, cancel: 'Cancelar' }
      }
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      validate={validateJustificativaCancelamento}
      renderBody={renderBody}
      decorators={decorator}
      initialValues={initialValues}
    />
  )
}
